import { useCallback, useEffect, useRef, useState } from "react";

import { mobile } from "@btnet/utilities-webview";

import { popupCenter } from "entity/setting/settingUtils";

import { type IStepUpAuthMessage, STEP_UP_AUTH_MESSAGE_TYPE } from "./StepUpAuthCallback";
import { useStepUpAuth } from "./StepUpAuthContext";

/**
 * Hook for programmatically requiring step-up authentication (legacy mode).
 * This uses current window redirect - for better UX with popups, use useStepUpPopup instead.
 *
 * @example Basic usage (redirects current window)
 * const { requireStepUp, getStepUpStatus } = useRequireStepUp();
 *
 * const handlePayment = async () => {
 *   const { hasStepUp, hasError } = await requireStepUp();
 *   if (!hasStepUp) {
 *     if (hasError) {
 *       // Handle error case (e.g., show error message)
 *       return;
 *     }
 *     // User will be redirected for step-up auth
 *     return;
 *   }
 *
 *   // Proceed with payment
 *   await processPayment();
 * };
 *
 * @example With custom return URL
 * const { hasStepUp, hasError } = await requireStepUp("/app/Bills/Bill/8675309");
 *
 * @example Check status without triggering step-up
 * const { hasStepUp, hasError } = await getStepUpStatus();
 */
export const useRequireStepUp = () => {
    const { checkStepUp, triggerStepUp } = useStepUpAuth();

    const requireStepUp = useCallback(
        async (
            returnUrl?: string,
        ): Promise<{
            hasStepUp: boolean;
            hasError: boolean;
        }> => {
            const { isValid, hasError } = await checkStepUp(true);

            // If there was an error checking step-up status, don't trigger step-up
            if (!isValid && !hasError) {
                triggerStepUp(returnUrl);
            }

            return { hasStepUp: isValid, hasError };
        },
        [checkStepUp, triggerStepUp],
    );

    const getStepUpStatus = useCallback(async (): Promise<{
        hasStepUp: boolean;
        hasError: boolean;
    }> => {
        const { isValid, hasError } = await checkStepUp(true);
        return { hasStepUp: isValid, hasError };
    }, [checkStepUp]);

    return { requireStepUp, getStepUpStatus };
};

export interface IStepUpPopupResult {
    success: boolean;
    error?: string;
}

/** Long enough to type an SMS code, short enough that an unanswered hand-off still errors. */
const NATIVE_STEP_UP_TIMEOUT_MS = 4 * 60 * 1000;

/**
 * Simplified hook for popup-based step-up authentication.
 * Opens a popup window synchronously to avoid popup blockers,
 * then the popup handles checking and performing authentication.
 *
 * @example
 * const { requireStepUpPopup, isAuthInProgress } = useStepUpPopup();
 *
 * const handleSendPayment = async () => {
 *   const result = await requireStepUpPopup();
 *   if (!result.success) {
 *     showError(result.error ?? "Authentication failed");
 *     return;
 *   }
 *   // Proceed with payment
 *   await sendPayment();
 * };
 */
export const useStepUpPopup = () => {
    const { checkStepUp } = useStepUpAuth();
    const [isAuthInProgress, setIsAuthInProgress] = useState(false);

    // Store pending promise resolve function
    const pendingResolveRef = useRef<((value: IStepUpPopupResult) => void) | null>(null);
    const authWindowRef = useRef<Window | null>(null);
    const authWindowCheckIntervalRef = useRef<number | null>(null);
    const nativeStepUpTimeoutRef = useRef<number | null>(null);
    const isMountedRef = useRef(true);

    // Cleanup function for auth window and listeners
    const cleanupAuth = useCallback(() => {
        if (authWindowCheckIntervalRef.current) {
            clearInterval(authWindowCheckIntervalRef.current);
            authWindowCheckIntervalRef.current = null;
        }
        if (nativeStepUpTimeoutRef.current) {
            clearTimeout(nativeStepUpTimeoutRef.current);
            nativeStepUpTimeoutRef.current = null;
        }
        if (authWindowRef.current && !authWindowRef.current.closed) {
            authWindowRef.current.close();
        }
        authWindowRef.current = null;
        pendingResolveRef.current = null;
        setIsAuthInProgress(false);
    }, []);

    // Handle authentication completion or cancellation
    const handleAuthComplete = useCallback(
        (success: boolean, error?: string) => {
            // Don't show notifications or update state if component unmounted
            if (!isMountedRef.current) {
                cleanupAuth();
                return;
            }

            if (pendingResolveRef.current) {
                pendingResolveRef.current({ success, error });
                pendingResolveRef.current = null;
            }
            cleanupAuth();

            // If successful, recheck step-up status to update context
            if (success) {
                void checkStepUp();
            }
        },
        [cleanupAuth, checkStepUp],
    );

    // Listen for messages from the popup window
    useEffect(() => {
        const handleMessage = (event: MessageEvent<IStepUpAuthMessage>) => {
            // Verify origin for security
            if (event.origin !== window.location.origin) {
                return;
            }

            // Check if this is a step-up auth message
            if (event.data?.type === STEP_UP_AUTH_MESSAGE_TYPE) {
                handleAuthComplete(event.data.success, event.data.error);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [handleAuthComplete]);

    // Cleanup on unmount - prevent notifications and close popup if component unmounts during auth
    useEffect(() => {
        // Since there's no main code to the function, the return actually fires on unmount! So this will detect unmount.
        return () => {
            isMountedRef.current = false;

            // If there's a pending auth, resolve it silently without showing notifications
            if (pendingResolveRef.current) {
                pendingResolveRef.current = null;
            }

            // Clean up the auth window and intervals
            cleanupAuth();
        };
    }, [cleanupAuth]);

    /**
     * Triggers step-up authentication in a popup window.
     * Opens the popup synchronously to avoid popup blockers,
     * then the popup handles checking if auth is needed.
     */
    const requireStepUpPopup = useCallback(
        (
            maxAgeMinutes?: number,
            preOpenedWindow?: Window | null,
            mfaRequirement?: "adaptive",
        ): Promise<IStepUpPopupResult> => {
            return new Promise((resolve) => {
                // Clean up any existing auth in progress
                if (pendingResolveRef.current) {
                    pendingResolveRef.current({
                        success: false,
                        error: "Authentication cancelled due to new request",
                    });
                }
                cleanupAuth();

                // Store the resolve function
                pendingResolveRef.current = resolve;
                setIsAuthInProgress(true);

                const callbackUrl = "/app/auth/step-up-callback";
                let popupUrl = `/api/login/LoginWithAuth0?stepUpReturnUrl=${encodeURIComponent(
                    callbackUrl,
                )}`;
                if (maxAgeMinutes !== undefined) {
                    popupUrl += `&maxAgeMinutes=${maxAgeMinutes}`;
                }
                if (mfaRequirement !== undefined) {
                    popupUrl += `&mfarequirement=${mfaRequirement}`;
                }

                // react-native-webview intercepts window.open() and returns null, which would
                // trip the popup-blocker check below. Hand off to native, which runs the
                // challenge and posts STEP_UP_AUTH_RESULT back to the listener above.
                if (mobile.isWebview) {
                    // Builds predating the native handler answer nothing, so nothing would
                    // ever settle this promise.
                    nativeStepUpTimeoutRef.current = window.setTimeout(() => {
                        handleAuthComplete(
                            false,
                            "Authentication timed out. Please try again on a computer.",
                        );
                    }, NATIVE_STEP_UP_TIMEOUT_MS);

                    mobile.sendToNative("requireStepUp", { popupUrl, callbackUrl });
                    return;
                }

                let authWindow: Window | null;

                // The option to pass a pre-opened window comes into play when
                // There are other async operations to perform, such as validation prior to stepping up.
                if (preOpenedWindow && !preOpenedWindow.closed) {
                    // Window was opened synchronously before async work to preserve the user gesture
                    // context — just navigate it to the step-up URL now.
                    preOpenedWindow.location.assign(popupUrl);
                    authWindow = preOpenedWindow;
                } else {
                    // CRITICAL: Open popup IMMEDIATELY (synchronously) to avoid popup blockers
                    // Use a unique window name to help detect if the window actually opened
                    const windowName = `stepup_${Date.now()}`;
                    authWindow = popupCenter(popupUrl, windowName, 600, 700, true);

                    // Check if window was blocked
                    // Some popup blockers return null, others return a window object with closed: true
                    // Some popup blockers end up returning an authWindow, and in my testing, we can compare the
                    // window.name property to see if it was set correctly
                    if (!authWindow || authWindow.closed || authWindow.name !== windowName) {
                        handleAuthComplete(
                            false,
                            "Pop-up blocked. Please allow pop-ups for this site.",
                        );
                        return;
                    }
                }

                authWindowRef.current = authWindow;

                // Poll to detect if user closed the popup early
                authWindowCheckIntervalRef.current = window.setInterval(() => {
                    if (authWindow.closed) {
                        handleAuthComplete(false, "Authentication cancelled by user");
                    }
                }, 500);
            });
        },
        [cleanupAuth, handleAuthComplete],
    );

    return {
        requireStepUpPopup,
        isAuthInProgress,
        authWindowRef,
    };
};
